import { createApi } from "@reduxjs/toolkit/query/react";

import {
    ICreateUserBody,
    ICreateUserResponse,
    IDeleteUserBody,
    IDeleteUserResponse,
    IGetUserByIdQuery,
    IGetUserByIdResponse,
    IGetUsersQuery,
    IGetUsersResponse,
    IChangeManagerRoleBody,
    IChangeManagerRoleResponse,
    IChangePersonnelRoleBody,
    IChangePersonnelRoleResponse,
    IResendInviteEmailBody,
    IResendInviteEmailResponse,
    ISetUserAssistantsBody,
    ISetUserAssistantsResponse,
    ISetUserEnabledBody,
    ISetUserEnabledResponse,
    IUpdateUserBody,
    IUpdateUserResponse,
} from "../../types/users";
import { getUsersQueryString } from "../../utils/queries";
import { baseQueryWithIntercept } from "../connection/api";

const ENDPOINT_PREFIX = "users";

export const usersApi = createApi({
    reducerPath: "usersApi",
    baseQuery: baseQueryWithIntercept,
    tagTypes: ["Users", "User"],
    endpoints: build => ({
        getUsers: build.query<IGetUsersResponse, IGetUsersQuery>({
            query: query => ({
                url: `${ENDPOINT_PREFIX}?${getUsersQueryString(query)}`,
            }),
            providesTags: (result, _error, _arg) => (result ? [...result.users.map(({ id }) => ({ type: "Users" as const, id })), "Users"] : ["Users"]),
        }),
        createUser: build.mutation<ICreateUserResponse, ICreateUserBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/`,
                method: "POST",
                body: data.data,
            }),
            invalidatesTags: ["Users"],
        }),
        addAdmin: build.mutation<ICreateUserResponse, ICreateUserBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/add-admin`,
                method: "POST",
                body: data.data,
            }),
            invalidatesTags: ["Users"],
        }),
        addManager: build.mutation<ICreateUserResponse, ICreateUserBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/add-manager`,
                method: "POST",
                body: data.data,
            }),
            invalidatesTags: ["Users"],
        }),
        addPersonnel: build.mutation<ICreateUserResponse, ICreateUserBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/add-personnel`,
                method: "POST",
                body: data.data,
            }),
            invalidatesTags: ["Users"],
        }),
        getUserById: build.query<IGetUserByIdResponse, IGetUserByIdQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.userId}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "User", id: arg.userId }],
        }),
        updateUser: build.mutation<IUpdateUserResponse, IUpdateUserBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.id}`,
                method: "PUT",
                body: data.data,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "User", id: arg.id }, "Users"],
        }),
        setUserAssistants: build.mutation<ISetUserAssistantsResponse, ISetUserAssistantsBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/set-user-assistants/${data.userId}`,
                method: "PATCH",
                body: {
                    assistantIds: data.assistantIds,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "User", id: arg.userId }, "Users"],
        }),
        deleteUser: build.mutation<IDeleteUserResponse, IDeleteUserBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.id}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "User", id: arg.id }, "Users"],
        }),
        deleteManager: build.mutation<IDeleteUserResponse, IDeleteUserBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/managers/${data.id}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "User", id: arg.id }, "Users"],
        }),
        deletePersonnel: build.mutation<IDeleteUserResponse, IDeleteUserBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/personnel/${data.id}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "User", id: arg.id }, "Users"],
        }),
        setPersonnelEnabled: build.mutation<ISetUserEnabledResponse, ISetUserEnabledBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/personnel/${data.id}/enabled`,
                method: "PATCH",
                body: { enabled: data.enabled },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "User", id: arg.id }, "Users"],
        }),
        setManagerEnabled: build.mutation<ISetUserEnabledResponse, ISetUserEnabledBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/managers/${data.id}/enabled`,
                method: "PATCH",
                body: { enabled: data.enabled },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "User", id: arg.id }, "Users"],
        }),
        resendInviteEmail: build.mutation<IResendInviteEmailResponse, IResendInviteEmailBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/resend-invite-email/${data.id}`,
                method: "POST",
            }),
        }),
        changeManagerRole: build.mutation<IChangeManagerRoleResponse, IChangeManagerRoleBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/change-manager-role/${data.userId}`,
                method: "PATCH",
                body: {
                    projectId: data.projectId,
                    role: data.role,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "User", id: arg.userId }, "Users"],
        }),
        changePersonnelRole: build.mutation<IChangePersonnelRoleResponse, IChangePersonnelRoleBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/change-personnel-role/${data.userId}`,
                method: "PATCH",
                body: {
                    projectId: data.projectId,
                    role: data.role,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "User", id: arg.userId }, "Users"],
        }),
        checkUsersByEmail: build.mutation<{ users: Record<string, { role: string; createdAt: string }> }, { emails: string[] }>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/check-existing-by-email`,
                method: "POST",
                body: data,
            }),
        }),
    }),
});

export const {
    useGetUsersQuery,
    useGetUserByIdQuery,
    useCreateUserMutation,
    useAddAdminMutation,
    useAddManagerMutation,
    useAddPersonnelMutation,
    useUpdateUserMutation,
    useSetUserAssistantsMutation,
    useDeleteUserMutation,
    useDeleteManagerMutation,
    useDeletePersonnelMutation,
    useSetPersonnelEnabledMutation,
    useSetManagerEnabledMutation,
    useResendInviteEmailMutation,
    useChangeManagerRoleMutation,
    useChangePersonnelRoleMutation,
    useCheckUsersByEmailMutation,
} = usersApi;
